// Browser PostHog wiring. initPosthog() runs once from main.tsx before React
// mounts; the identity helpers are driven by <PosthogIdentity> as Clerk auth
// resolves. All config is build-time inlined from VITE_* env (loaded from SSM
// /apps/dashboard/ at release). Reuses the same PostHog project as the
// marketing site, proxied through n.gated.cc.
import posthog, { DisplaySurveyType } from "posthog-js";

// The dashboard's feedback survey (sidebar footer). It's a Popover survey in
// PostHog whose only display condition is a `feedback_requested` event we never
// fire, so it never auto-pops — we force-render it on click instead, with
// ignoreConditions/ignoreDelay. PostHog owns the form and stores responses
// (queryable under Surveys; it auto-captures `survey shown/sent/dismissed`).
const FEEDBACK_SURVEY_ID = "019ebd8f-4ad2-0000-ab81-74dcf0554050";

export function initPosthog(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  // No key (local dev / preview) → PostHog stays uninitialised and every
  // posthog.* call below is a safe no-op.
  if (!key) return;

  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST as string | undefined,
    ui_host: "https://us.posthog.com",
    defaults: "2026-01-30",
    // Sentry owns error reporting in this app — don't double-capture.
    capture_exceptions: false,
    // Session replay is ON so we can see how users move through the product,
    // but every input/textarea/select value is masked by the recorder BEFORE
    // it leaves the browser (maskAllInputs). We render no secrets, but hard-
    // masking inputs is defense-in-depth: target URLs, bearer tokens, or OAuth
    // client secrets typed into identity forms never reach PostHog. Text stays
    // visible (so navigation/usage is legible); add the `ph-no-capture` class
    // to any element whose *text* must also be redacted from replays.
    session_recording: {
      maskAllInputs: true,
    },
    // Console logs can carry request/response detail — keep them out of replays.
    enable_recording_console_log: false,
    debug: import.meta.env.DEV,
  });
}

// Ties the session to the signed-in Clerk user and groups events by org, so
// product analytics can segment by tenant. org_id mirrors the Sentry tag and
// the platform/worker org tag. It is the Clerk organization id (not a secret).
export function setPosthogUser(
  userId: string,
  orgId: string | null,
  email: string | null,
): void {
  // distinct_id stays the stable Clerk user id; email rides as a person
  // property so people are searchable/legible in PostHog without making the
  // identity churn if the address ever changes.
  posthog.identify(userId, email ? { email } : undefined);
  if (orgId) posthog.group("organization", orgId);
}

// On sign-out, drop the identity and start a fresh anonymous session so the
// next user isn't attributed to the previous one.
export function clearPosthogUser(): void {
  posthog.reset();
}

// Show the feedback survey on demand. Surveys aren't available on page load, so
// gate on onSurveysLoaded before displaying. No-op when PostHog is
// uninitialised (local dev / no key) — the callback simply never fires.
export function openFeedbackSurvey(): void {
  posthog.onSurveysLoaded(() => {
    posthog.displaySurvey(FEEDBACK_SURVEY_ID, {
      displayType: DisplaySurveyType.Popover,
      ignoreConditions: true,
      ignoreDelay: true,
    });
  });
}
